import { useEffect, useMemo, useReducer } from 'react';
import { ActionRegistry } from '@packages/table-builder';
import type { ActionBlock, AnyBlock, BlockRendererSharedProps, ResolvedAnyBlock } from '../types/blocks';
import { getActionBuilderMetaDependencyKey } from '../types/blocks';
import { BlockLoadState } from './BlockLoadState';
import { renderBlockTree } from '../core/ControlRenderer';
import { useStudioPreview } from '../core/StudioPreviewContext';
import { isStudioPreviewEntityId } from '../core/studioPreviewEntity';

export interface ActionBlockRendererProps extends BlockRendererSharedProps {
  block: ActionBlock;
  handle?: import('../core/useControlRendererRef').ControlRendererHandle;
}

interface State {
  status: 'idle' | 'loading' | 'ready' | 'error';
  result: AnyBlock | ResolvedAnyBlock | null;
  error: string | null;
}

const initial: State = { status: 'idle', result: null, error: null };

type ReducerAction =
  | { type: 'LOADING' }
  | { type: 'READY'; result: AnyBlock | ResolvedAnyBlock }
  | { type: 'ERROR'; error: string };

function reducer(_: State, action: ReducerAction): State {
  switch (action.type) {
    case 'LOADING': return { status: 'loading', result: null, error: null };
    case 'READY': return { status: 'ready', result: action.result, error: null };
    case 'ERROR': return { status: 'error', result: null, error: action.error };
  }
}

export function ActionBlockRenderer({ block, params, meta, onSuccess, onError, onCancel, handle }: ActionBlockRendererProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initial);
  const { isStudioPreview } = useStudioPreview();

  const isLoading = state.status === 'loading' || state.status === 'idle';
  const builderMetaKey = useMemo(
    () => getActionBuilderMetaDependencyKey(meta, block.blockMeta),
    [meta, block.blockMeta],
  );

  useEffect(() => {
    handle?._setLoading(isLoading);
  }, [isLoading, handle]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!block.actionId) {
        dispatch({ type: 'ERROR', error: 'No actionId specified on action block.' });
        return;
      }

      // In studio preview mode without a valid entity ID, skip builder actions
      // that pre-fetch data (BUILD_*_VIEW pattern). Lightweight builders that just
      // return view descriptors (BUILD_*_SUMMARY, BUILD_*_TABS) are still executed.
      const needsEntityId = block.actionId.startsWith('BUILD_') && block.actionId.includes('_VIEW');
      if (isStudioPreview && needsEntityId && !isStudioPreviewEntityId(params?.id)) {
        dispatch({
          type: 'ERROR',
          error: 'Studio preview: provide an Entity ID to load data for this view.',
        });
        return;
      }

      dispatch({ type: 'LOADING' });
      try {
        await ActionRegistry.ensureActions([block.actionId]);
        const effectiveMeta = block.blockMeta != null ? { ...meta, ...block.blockMeta } : meta;
        const raw = await ActionRegistry.executeBuilder(block.actionId, { params, meta: effectiveMeta });

        if (cancelled) return;

        if (raw && typeof raw === 'object' && 'block' in raw && (raw as any).block) {
          dispatch({ type: 'READY', result: (raw as any).block as AnyBlock });
        } else {
          dispatch({
            type: 'ERROR',
            error: `Action "${block.actionId}" did not return a valid block descriptor ({ block: ... }).`,
          });
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        if (isStudioPreview && (message === 'Failed to fetch' || message.includes('Network'))) {
          dispatch({
            type: 'ERROR',
            error: 'Studio preview: backend unavailable or Entity ID missing. Ensure the API is running and provide a valid Entity ID.',
          });
        } else {
          dispatch({ type: 'ERROR', error: message });
        }
      }
    }

    void run();
    return () => { cancelled = true; };
  }, [block.actionId, builderMetaKey, params, isStudioPreview]);

  const error = state.status === 'error' ? state.error : null;
  const showInlineLoading = !handle && isLoading;

  return (
    <BlockLoadState isLoading={showInlineLoading} error={error}>
      {state.result != null && renderBlockTree(state.result, { params, meta, onSuccess, onError, onCancel, handle })}
    </BlockLoadState>
  );
}
